/**
 * THE ONE READING OF A SESSION-LEVEL REFUSAL, for App and Book.
 *
 * Backend refuses a request before any resolver for five session reasons,
 * and for an App Check token it did not accept (APP_CHECK_REJECTED),
 * (final shapes, Backend 2026-10-06, client-v1 1.6.0 / professional-v1
 * 1.27.0; local stand-in fixture `fixtures/session-refusals.local.json`). This says which one an Apollo error
 * is, and nothing else. It never acts: the Apollo link in `sessionLink.ts`
 * decides what to do: only for a credentialed request, except an App Check
 * refusal (APP_CHECK_REJECTED), which it records for any credential.
 *
 * READ ORDER, and why. `extensions.reason` first, then `accessRecovery`, then
 * the shape codes, then the bare HTTP status. NEVER the message text, which is
 * for people and may change or be translated.
 *
 * BOTH WIRE PATHS. A 401/503/400 sent as `application/json` reaches Apollo 4
 * as a `ServerError` whose body is only `bodyText` (Apollo 4.2 has no parsed
 * `result`; one is read if present, for tolerance). The same status sent as
 * `application/graphql-response+json`, or a 200 with errors, reaches it as
 * `CombinedGraphQLErrors` with `errors`. Both are read here, with the same
 * entries, so a change of content type on Backend changes nothing. A 401
 * carrying partial data (RECENT_SIGN_IN_REQUIRED) is still a ServerError over
 * `application/json`, and its `errors[]` is read from `bodyText` the same way.
 *
 * An unknown `reason` is treated exactly as no reason.
 *
 * No Apollo or Firebase import: this is a pure reader, and cheap to test.
 */

export const SESSION_REVOKED = "SESSION_REVOKED";
/**
 * Backend's App Check refusal (access-refusals.json
 * contextRefusals.appCheckRejected): HTTP 403, no data, code
 * APP_CHECK_REJECTED with RETRY_PRESERVING_SESSION, sent by /api/v1/core and
 * /api/v1/app for a missing or rejected X-Firebase-AppCheck BEFORE identity is
 * checked.
 */
export const APP_CHECK_REJECTED = "APP_CHECK_REJECTED";
export const RECENT_SIGN_IN_REQUIRED = "RECENT_SIGN_IN_REQUIRED";

/** A request document the server would not run. A client bug, never retried. */
export type ShapeRefusalCode = "GRAPHQL_VALIDATION_FAILED" | "GRAPHQL_PARSE_FAILED";

export type SessionRefusal =
  /**
   * The session was ended (sign-out everywhere, a password change, an admin
   * action). Sign out locally and route to sign-in. NEVER refresh and retry:
   * a refreshed ID token keeps the old `auth_time`, so it is refused again.
   */
  | { kind: "session-revoked" }
  /**
   * A sensitive operation (apiCore account.endOtherSessions) needs a fresh
   * sign-in. HTTP 401 WITH partial data (`data.account: null`); nothing was
   * written. The session is still good: the CALLER re-authenticates with
   * credentials and calls ONCE more. Never a global sign-out, never a token
   * refresh. Exposed for the reset-page step (a later batch) through
   * `isRecentSignInRequired`.
   */
  | { kind: "recent-sign-in-required" }
  /**
   * No token, or a token that did not verify, with no reason or a reason this
   * build does not know. Refresh once through the Firebase SDK; sign out only
   * if the SDK itself says the identity is invalid. `source` says whether
   * Backend sent REAUTHENTICATE metadata or this is a 401 with no
   * errors[].extensions (the deployed apiCore gateway's expired-token 401).
   */
  | { kind: "refresh-token"; source: "metadata" | "status" | "gateway" }
  /**
   * The session is fine; the server could not check it (503). Keep it. Any
   * retry is the person's: no automatic retry, no backoff.
   */
  | { kind: "retry-preserving-session"; code: string | null; retryable: boolean | null }
  /**
   * The APP was refused, not the person: no App Check token, or one Backend
   * rejected, checked before identity. Keep the session, never refresh the ID
   * token (it says nothing about identity), never replay. The page's Try again
   * must reload: in the same page the App Check SDK holds its throttle, so an
   * in-page retry is refused again. Only code APP_CHECK_REJECTED WITH
   * RETRY_PRESERVING_SESSION on the same entry; anything else follows the
   * ordinary rules.
   */
  | { kind: "app-check-rejected"; retryable: boolean | null }
  /** A valid session refused here. The owning screen leaves; nothing session-level. */
  | { kind: "leave-surface"; code: string | null }
  /** The document was refused (validation, or the parser's token cap). A client bug. */
  | { kind: "shape-refused"; code: ShapeRefusalCode };

interface Entry {
  extensions?: unknown;
}

const SHAPE_CODES: ReadonlySet<string> = new Set<ShapeRefusalCode>([
  "GRAPHQL_VALIDATION_FAILED",
  "GRAPHQL_PARSE_FAILED",
]);

/** At most this many entries are read from each source: a hostile or broken body cannot make reading it expensive. */
export const MAX_ENTRIES_PER_SOURCE = 50;

/** Append up to MAX_ENTRIES_PER_SOURCE entries of `list`. A loop, never a spread: a huge array cannot overflow the stack. */
function appendCapped(into: Entry[], list: unknown): void {
  if (!Array.isArray(list)) return;
  const limit = Math.min(list.length, MAX_ENTRIES_PER_SOURCE);
  for (let i = 0; i < limit; i += 1) into.push(list[i] as Entry);
}

function errorsOf(value: unknown): unknown {
  if (!value || typeof value !== "object") return undefined;
  return (value as { errors?: unknown }).errors;
}

/**
 * Every GraphQL error entry an Apollo 3/4 error, or a raw body, carries.
 * Never throws: a non-GraphQL or unparseable body (a gateway's JSON, HTML,
 * text) simply contributes no entries.
 */
export function sessionErrorEntries(error: unknown): Entry[] {
  if (!error || typeof error !== "object") return [];
  const candidate = error as {
    graphQLErrors?: unknown;
    bodyText?: unknown;
    result?: unknown;
    cause?: unknown;
  };
  const entries: Entry[] = [];
  try {
    appendCapped(entries, errorsOf(candidate));
    appendCapped(entries, candidate.graphQLErrors);
    appendCapped(entries, errorsOf(candidate.result));
  } catch {
    // A getter that throws is not a session contract either.
  }
  if (typeof candidate.bodyText === "string") {
    try {
      appendCapped(entries, errorsOf(JSON.parse(candidate.bodyText)));
    } catch {
      // A gateway's HTML or text body is not a session contract.
    }
  }
  if (candidate.cause && typeof candidate.cause === "object") entries.push(candidate.cause as Entry);
  return entries;
}

function extensionsOf(entry: Entry): Record<string, unknown> | null {
  const extensions = entry?.extensions;
  return extensions && typeof extensions === "object" ? (extensions as Record<string, unknown>) : null;
}

/** `statusCode` (Apollo 4 ServerError / ServerParseError) or the fetch Response's. */
export function httpStatusOf(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;
  const candidate = error as { statusCode?: unknown; response?: { status?: unknown } | null };
  if (typeof candidate.statusCode === "number") return candidate.statusCode;
  if (typeof candidate.response?.status === "number") return candidate.response.status;
  return null;
}

export interface ClassifyOptions {
  /**
   * The HTTP status when the error itself does not carry one: on the
   * application/graphql-response+json path Apollo parses a non-2xx body into
   * CombinedGraphQLErrors, and the status is only on the operation context
   * (`operation.getContext().response.status`). The session link passes it.
   * Used ONLY for the last step, a 401 with no extensions at all; a reason
   * is decided from the entry's extensions alone.
   */
  readonly status?: number | null;
}

/**
 * Which session refusal `error` is, or null when it is not one (an ordinary
 * GraphQL error, a network failure, a 5xx without metadata).
 */
export function classifySessionRefusal(error: unknown, options: ClassifyOptions = {}): SessionRefusal | null {
  const all = sessionErrorEntries(error)
    .map(extensionsOf)
    .filter((value): value is Record<string, unknown> => value !== null);
  const status = httpStatusOf(error) ?? (typeof options.status === "number" ? options.status : null);

  // 1. The reason, which is the most specific thing Backend says, honoured
  // ONLY in the exact shape Backend sends it. Backend's contract rule (its
  // READMEs, 2026-10-06): DECIDE FROM EACH ERROR'S OWN EXTENSIONS, NOT THE
  // HTTP STATUS. So the SAME entry must carry code UNAUTHENTICATED,
  // accessRecovery REAUTHENTICATE and the reason; the status is not read
  // here. Anything else (a bare reason, a reason on RETRY_PRESERVING_SESSION,
  // a reason with another code) falls through to the ordinary handling below.
  for (const ext of all) {
    if (ext.code !== "UNAUTHENTICATED" || ext.accessRecovery !== "REAUTHENTICATE") continue;
    if (ext.reason === SESSION_REVOKED) return { kind: "session-revoked" };
    if (ext.reason === RECENT_SIGN_IN_REQUIRED) return { kind: "recent-sign-in-required" };
  }

  // 2. The recovery instruction. An unknown reason lands here with its
  // REAUTHENTICATE and gets the ordinary refresh-once handling.
  for (const ext of all) {
    const code = typeof ext.code === "string" ? ext.code : null;
    switch (ext.accessRecovery) {
      case "REAUTHENTICATE":
        return { kind: "refresh-token", source: "metadata" };
      case "RETRY_PRESERVING_SESSION":
        if (code === APP_CHECK_REJECTED) {
          return { kind: "app-check-rejected", retryable: typeof ext.retryable === "boolean" ? ext.retryable : null };
        }
        return {
          kind: "retry-preserving-session",
          code,
          retryable: typeof ext.retryable === "boolean" ? ext.retryable : null,
        };
      case "LEAVE_SURFACE":
        return { kind: "leave-surface", code };
      default:
        break;
    }
  }

  // 3. The shape refusals carry no recovery: nothing the person can do helps.
  for (const ext of all) {
    if (typeof ext.code === "string" && SHAPE_CODES.has(ext.code)) {
      return { kind: "shape-refused", code: ext.code as ShapeRefusalCode };
    }
  }

  // 4. A 401 with no errors[].extensions: REAUTHENTICATE with no reason.
  // Force-refresh once; sign out only if the SDK says the identity is invalid.
  // The commonest real one is the DEPLOYED apiCore gateway refusing an
  // expired, missing, malformed or unknown-key token before the server runs
  // (Backend's rc.3 capture `gateway401`): HTTP 401, a JSON body with a
  // NUMERIC `code` of 401 and no `errors` array, recognised by that STRUCTURE
  // as `source: "gateway"`; its message wording is the gateway's and is never
  // read. It comes from /api/v1/core only: the /app session link refuses the
  // gateway path (sessionLink.ts `gateway: false`).
  if (status === 401) return { kind: "refresh-token", source: isGatewayBody(error) ? "gateway" : "status" };
  return null;
}

/** HTTP 401's gateway shape: a JSON object, `code` the number 401, no `errors`. */
function isGatewayBody(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const { bodyText, result } = error as { bodyText?: unknown; result?: unknown };
  let body: unknown = result;
  if (typeof bodyText === "string") {
    try {
      body = JSON.parse(bodyText);
    } catch {
      return false;
    }
  }
  if (!body || typeof body !== "object" || Array.isArray(body)) return false;
  const { code } = body as { code?: unknown };
  return typeof code === "number" && code === 401 && !("errors" in body);
}

/** RECENT_SIGN_IN_REQUIRED, for the caller that must re-authenticate and call once more. */
export function isRecentSignInRequired(error: unknown, options: ClassifyOptions = {}): boolean {
  return classifySessionRefusal(error, options)?.kind === "recent-sign-in-required";
}
